"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  findIrrs,
  nearestIrr,
  npv,
  npvProfile,
  paybackPeriod,
  profitabilityIndex,
  signChanges,
} from "@/lib/tool-kit/finance/cashflow";
import {
  MAX_FLOWS,
  parseCashFlows,
} from "@/lib/tool-kit/finance/cashflow-input";

/** English strings. A translated content file overrides these through `ui`. */
export const NPV_CALCULATOR_STRINGS = {
  inputsHeading: "Your cash flows",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  rate: "Discount rate per period (%)",
  flowsLabel: "Cash flows — one per line, period 0 first",
  flowsHint:
    "Outflows are negative: -40000 then 8000, 9200 and so on. Period 0 is not discounted. Enter 0 for a period with no cash flow.",
  flowsDetected: "one:{count} cash flow|other:{count} cash flows",
  resultsHeading: "Results",
  npv: "Net present value (NPV)",
  irr: "Internal rate of return (IRR)",
  irrPerPeriod: "per period",
  profitabilityIndex: "Profitability index",
  payback: "Payback period",
  discountedPayback: "Discounted payback",
  periodsUnit: "one:{count} period|other:{count} periods",
  neverPaysBack: "Not within these periods",
  noIrr: "None",
  npvPositive:
    "The NPV is positive: at this discount rate the cash flows are worth more than they cost.",
  npvNegative:
    "The NPV is negative: at this discount rate the cash flows are worth less than they cost.",
  npvZero:
    "The NPV is zero: the discount rate equals the IRR, so the project exactly earns its required return.",
  multipleIrr:
    "These cash flows change sign {changes} times, so more than one IRR can exist. Found: {list}. Rely on NPV at your discount rate rather than on an IRR here.",
  possibleMultipleIrr:
    "These cash flows change sign {changes} times, so more than one IRR is possible; only the one found is shown. Rely on NPV rather than on an IRR here.",
  noIrrNote:
    "No IRR exists between −99% and 10,000% per period. This happens when the cash flows never change sign — there is no outlay, or no return.",
  convention:
    "Period 0 is not discounted; the cash flow in period t is divided by (1 + rate)^t. Excel's NPV() function discounts its first value by one period, so NPV(rate, later flows) + period-0 flow equals this result.",
  tableHeading: "Period by period",
  periodColumn: "Period",
  flowColumn: "Cash flow",
  factorColumn: "Discount factor",
  presentColumn: "Present value",
  cumulativeColumn: "Cumulative cash flow",
  cumulativePvColumn: "Cumulative PV",
  chartTitle: "NPV profile",
  chartXTitle: "Discount rate (%)",
  chartYTitle: "NPV",
  chartLabel: "NPV profile chart",
  chartDescription:
    "Line chart of net present value against the discount rate, from {from}% to {to}%. NPV is {first} at the left edge and {last} at the right edge.",
  seriesNpv: "NPV",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  needFlows: "Enter at least two cash flows, one per line, to see the result.",
  invalidFlow: "“{text}” is not a number. Enter each cash flow as a number.",
  invalidRate: "Enter a discount rate greater than −100%.",
  tooMany: "Enter at most {max} cash flows.",
} as const;

type Strings = typeof NPV_CALCULATOR_STRINGS;

const EXAMPLE_FLOWS = "-40000\n8000\n9200\n10000\n12000\n14500";
const PROFILE_STEPS = 20;

/** A rate axis in whole steps of 5%: wide enough to show every IRR and the chosen rate. */
function profileRange(irrs: number[], rate: number): [number, number] {
  const low = Math.min(0, ...irrs.map((irr) => irr * 1.25), rate);
  const high = Math.max(0.2, ...irrs.map((irr) => irr * 1.5), rate * 1.5);
  const from = Math.max(-0.9, Math.floor(low * 20) / 20);
  const to = Math.min(5, Math.ceil(high * 20) / 20);
  return [from, to];
}

function NpvProfile({
  flows,
  irrs,
  rate,
  strings,
}: {
  flows: number[];
  irrs: number[];
  rate: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [from, to] = profileRange(irrs, rate);
  const points = npvProfile(flows, from, to, PROFILE_STEPS);
  const label = (r: number) => fmt(r * 100, { maximumFractionDigits: 2 });
  const rows: LineRow[] = points.map((p) => ({
    label: label(p.rate),
    x: p.rate * 100,
    values: [p.npv],
  }));
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle: strings.chartYTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  const value = (v: number) => fmt(v, { maximumFractionDigits: 0 });
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.chartXTitle, strings.seriesNpv],
        ...rows.map((row) => [row.label, ...row.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.chartTitle, "npv-profile")}
      labels={strings}
      note=""
      table={null}
      title={strings.chartTitle}
    >
      <LineChart
        description={t(strings.chartDescription, {
          from: label(from),
          to: label(to),
          first: value(points[0].npv),
          last: value(points.at(-1)?.npv ?? 0),
        })}
        fmt={fmt}
        id="npv-chart"
        label={strings.chartLabel}
        markers={false}
        names={[strings.seriesNpv]}
        numeric
        rows={rows}
        style={style}
        zero
      />
    </ChartFigure>
  );
}

function IrrNotes({
  flows,
  irrs,
  strings,
}: {
  flows: number[];
  irrs: number[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const changes = signChanges(flows);
  const percent = (r: number) =>
    `${fmt(r * 100, { maximumFractionDigits: 4 })}%`;
  const values = {
    changes: fmt(changes),
    list: irrs.map(percent).join("; "),
  };
  if (irrs.length === 0) {
    return <Notice>{strings.noIrrNote}</Notice>;
  }
  if (irrs.length > 1) {
    return <Notice>{t(strings.multipleIrr, values)}</Notice>;
  }
  if (changes > 1) {
    return <Notice>{t(strings.possibleMultipleIrr, values)}</Notice>;
  }
  return null;
}

function Results({
  flows,
  rate,
  currency,
  strings,
}: {
  flows: number[];
  rate: number;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const money = useMoney(currency);
  const value = npv(rate, flows);
  const irrs = findIrrs(flows);
  const main = nearestIrr(irrs);
  const pi = profitabilityIndex(rate, flows);
  const periods = (n: number | null) =>
    n === null
      ? strings.neverPaysBack
      : tPlural(strings.periodsUnit, locale, Math.round(n * 100) / 100, {
          count: fmt(n, { maximumFractionDigits: 2 }),
        });
  const rounded = Math.round(value * 100) / 100;
  let verdict: string = strings.npvZero;
  if (rounded > 0) {
    verdict = strings.npvPositive;
  } else if (rounded < 0) {
    verdict = strings.npvNegative;
  }
  let cumulative = 0;
  let cumulativePv = 0;
  const rows = flows.map((flow, period) => {
    const factor = (1 + rate) ** -period;
    cumulative += flow;
    cumulativePv += flow * factor;
    return [
      fmt(period),
      money(flow),
      fmt(factor, { maximumFractionDigits: 6 }),
      money(flow * factor),
      money(cumulative),
      money(cumulativePv),
    ];
  });
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.npv} value={money(value)} />
        <StatCard
          label={`${strings.irr} — ${strings.irrPerPeriod}`}
          value={
            main === null
              ? strings.noIrr
              : `${fmt(main * 100, { maximumFractionDigits: 4 })}%`
          }
        />
        <StatCard
          label={strings.profitabilityIndex}
          value={pi === null ? "—" : fmt(pi, { maximumFractionDigits: 4 })}
        />
        <StatCard
          label={strings.payback}
          value={periods(paybackPeriod(flows))}
        />
        <StatCard
          label={strings.discountedPayback}
          value={periods(paybackPeriod(flows, rate))}
        />
      </StatGrid>
      <p className="text-foreground text-sm">{verdict}</p>
      <IrrNotes flows={flows} irrs={irrs} strings={strings} />
      <NpvProfile flows={flows} irrs={irrs} rate={rate} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.periodColumn,
          strings.flowColumn,
          strings.factorColumn,
          strings.presentColumn,
          strings.cumulativeColumn,
          strings.cumulativePvColumn,
        ]}
        rows={rows}
      />
    </section>
  );
}

export default function NpvCalculatorWidget() {
  const strings = useToolUi(NPV_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [currency, setCurrency] = useState("USD");
  const [rateText, setRateText] = useState("8");
  const [raw, setRaw] = useState(EXAMPLE_FLOWS);

  const parsed = parseCashFlows(raw);
  const rate = parseNumberInput(rateText);
  let problem: string | null = null;
  if (parsed.invalid !== null) {
    problem = t(strings.invalidFlow, { text: parsed.invalid });
  } else if (rate === null || rate <= -100) {
    problem = strings.invalidRate;
  } else if (parsed.flows.length > MAX_FLOWS) {
    problem = t(strings.tooMany, { max: fmt(MAX_FLOWS) });
  } else if (parsed.flows.length < 2) {
    problem = strings.needFlows;
  }

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="npv-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="npv-rate"
            label={strings.rate}
            numeric
            onChange={setRateText}
            value={rateText}
          />
        </div>
        <TextAreaField
          hint={strings.flowsHint}
          id="npv-flows"
          label={strings.flowsLabel}
          onChange={setRaw}
          rows={7}
          value={raw}
        />
        <p aria-live="polite" className={HINT}>
          {tPlural(strings.flowsDetected, locale, parsed.flows.length, {
            count: fmt(parsed.flows.length),
          })}
        </p>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {problem === null && rate !== null ? (
        <Results
          currency={currency}
          flows={parsed.flows}
          rate={rate / 100}
          strings={strings}
        />
      ) : (
        <Notice>{problem}</Notice>
      )}
    </div>
  );
}
